import React from 'react';
import cx from 'classnames';
import { IconButton } from '@JohnLewisPartnership/jl-design-system/dist/elements/button/IconButton';
import { ReactComponent as WishlistIcon } from '../../assets/icons/wishlist.svg';
import styles from './AddToWishListIcon.module.scss';
import Cookies from 'js-cookie';
import { redirectToSignIn } from '../../helpers/redirect/redirect';
import { SecondaryButton } from '@JohnLewisPartnership/jl-design-system/dist/elements/button/Button';
import { WishlistFilled } from '@JohnLewisPartnership/jl-design-system/dist/components/icons/WishlistFilled';
import { WishlistOutlined } from '@JohnLewisPartnership/jl-design-system/dist/components/icons/WishlistOutlined';
import { getExperiencesFromWindow, isFlagSet } from '../../helpers/featureFlags/featureFlags';
import { FEATURE_FLAGS } from '../../helpers/featureFlags/constants';
import { GUEST_WISH_LIST_EXPERIENCE } from '../../stores/constants';
import { fetchMonetateExperiences, postMonetateImpressionByName } from '../../helpers/monetate/monetate';
import { getExperiences } from '../../helpers/productDetails/productDetails';

const Wishlist24PxIcon: React.FC = () => <WishlistIcon />;
Wishlist24PxIcon.displayName = 'Wishlist24PxIcon';
export interface AddToWishListIconProps {
    product?: WishListIconProduct;
    inWishList?: boolean;
    isSubmitting?: boolean;
    handleOnClick: (product: WishListIconProduct, remove: boolean) => Promise<void>;
    mode?: string;
}

export const AddToWishListIcon: React.FC<AddToWishListIconProps> = ({
    handleOnClick,
    product,
    inWishList = false,
    isSubmitting = false,
    mode = undefined,
}: AddToWishListIconProps) => {
    if (!product) return null;

    const isPdpIcon = mode === 'icon-placeholder-pdp';

    if (isPdpIcon) {
        const useOutOfStockButton = product.useOutOfStockButton;

        if (useOutOfStockButton) {
            const wishListIcon = inWishList ? <WishlistFilled /> : <WishlistOutlined />;

            return (
                <SecondaryButton
                    data-testid="add-to-wish-list-button"
                    onClick={async (): Promise<void> => {
                        const windowExperiences = getExperiencesFromWindow();
                        const isGuest = !Cookies.get('shopperId');
                        const experiences = getExperiences();
                        const headerExperiences =
                            windowExperiences.length === 0 && experiences.length === 0
                                ? await fetchMonetateExperiences()
                                : [];
                        if (isGuest) {
                            postMonetateImpressionByName(
                                GUEST_WISH_LIST_EXPERIENCE,
                                windowExperiences || headerExperiences,
                            );
                        }

                        if (isGuest && !isFlagSet(FEATURE_FLAGS.GUEST_WISHLIST, experiences || headerExperiences)) {
                            // Guest user
                            redirectToSignIn(product.id, mode);
                            return;
                        }
                        handleOnClick?.(product, inWishList);
                    }}
                    className={cx(styles.addToWishListButton, {
                        [styles.active]: inWishList,
                    })}
                    submitting={isSubmitting}
                >
                    <div className={styles.wishListButtonContent}>
                        {wishListIcon}
                        <div>{inWishList ? 'Added to wish list' : 'Add to wish list'}</div>
                    </div>
                </SecondaryButton>
            );
        } else {
            return (
                <SecondaryButton
                    data-testid="add-to-wish-list-icon"
                    onClick={async (): Promise<void> => {
                        const windowExperiences = getExperiencesFromWindow();
                        const experiences = getExperiences();
                        const headerExperiences =
                            windowExperiences.length === 0 && experiences.length === 0
                                ? await fetchMonetateExperiences()
                                : [];
                        const isGuest = !Cookies.get('shopperId');

                        if (isGuest) {
                            postMonetateImpressionByName(
                                GUEST_WISH_LIST_EXPERIENCE,
                                windowExperiences || headerExperiences,
                            );
                        }

                        if (isGuest && !isFlagSet(FEATURE_FLAGS.GUEST_WISHLIST, experiences || headerExperiences)) {
                            // Guest user
                            redirectToSignIn(product.id, mode);
                            return;
                        }
                        handleOnClick?.(product, inWishList);
                    }}
                    className={cx(styles.addToWishListIcon, styles.isPdpIcon, {
                        [styles.active]: inWishList,
                        [styles.loading]: isSubmitting,
                    })}
                    IconComponent={Wishlist24PxIcon}
                    ariaLabel="add to your wishlist"
                >
                    <Wishlist24PxIcon />
                </SecondaryButton>
            );
        }
    }
    return (
        <IconButton
            data-testid="add-to-wish-list-icon"
            onClick={async (): Promise<void> => {
                const windowExperiences = getExperiencesFromWindow();
                const experiences = getExperiences();
                const headerExperiences =
                    windowExperiences.length === 0 && experiences.length === 0 ? await fetchMonetateExperiences() : [];
                const isGuest = !Cookies.get('shopperId');

                if (isGuest) {
                    postMonetateImpressionByName(GUEST_WISH_LIST_EXPERIENCE, windowExperiences || headerExperiences);
                }

                if (
                    mode !== 'icon-placeholder-wishlist-recs' &&
                    isGuest &&
                    !isFlagSet(FEATURE_FLAGS.GUEST_WISHLIST, experiences || headerExperiences)
                ) {
                    // Guest user without guest wishlist feature flag
                    redirectToSignIn(product.id, mode);
                    return;
                }
                handleOnClick?.(product, inWishList);
            }}
            className={cx(styles.addToWishListIcon, {
                [styles.active]: inWishList,
                [styles.loading]: isSubmitting,
            })}
            IconComponent={Wishlist24PxIcon}
            size="small"
            ariaLabel="add to your wishlist"
        />
    );
};

export default AddToWishListIcon;
